import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
          data-testid="terms-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-blood-400 mb-2" data-testid="terms-title">
          TERMS OF SERVICE
        </h1>
        <p className="text-stone-500 text-sm mb-8">Last updated: March 27, 2026</p>

        <div className="space-y-6 text-stone-300 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Anvil & Ash ("the App"), you agree to be bound by these Terms
              of Service. If you do not agree to these terms, do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">2. Description of Service</h2>
            <p>
              Anvil & Ash is a fitness tracking and workout planning application that provides:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>AI-generated personalized workout plans</li>
              <li>Workout logging and progress tracking</li>
              <li>Nutrition tracking and meal logging</li>
              <li>Social fitness features (battles, friend connections)</li>
              <li>Curated product recommendations via affiliate links</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">3. User Accounts</h2>
            <ul className="list-disc list-inside space-y-1 text-stone-400">
              <li>You must provide accurate information when creating an account.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>You must be at least 13 years old to use this service.</li>
              <li>One account per person. Sharing accounts is not permitted.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4. Medical Disclaimer &amp; Health Information</h2>
            <p className="text-yellow-400/90 font-medium">
              IMPORTANT: Anvil &amp; Ash is operated by individuals who are not physicians,
              licensed medical professionals, registered dietitians, physical therapists, or
              certified personal trainers. Nothing in the App constitutes medical advice,
              diagnosis, treatment, prescription, or therapy of any kind.
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 text-stone-400">
              <li>The App is a general-purpose self-logging tool for personal fitness tracking — not a medical, healthcare, diagnostic, or rehabilitation service.</li>
              <li>Always consult a qualified physician before starting any new exercise or nutrition program, especially if you have any pre-existing condition, are pregnant, postpartum, recovering from surgery, taking medication, or have been sedentary.</li>
              <li>AI-generated workout plans, calorie estimates, nutrition data, recipe analyses, and recovery routines are approximations for informational purposes only. We make no guarantee of accuracy, completeness, suitability, or any specific outcome.</li>
              <li>The App does not measure or record exercise scientifically. It is not approved by the FDA, AMA, or any medical or regulatory body.</li>
              <li>You exercise and use this App entirely at your own risk. We are not liable for any injury, illness, death, property damage, or adverse outcome.</li>
              <li>Stop immediately if you experience chest pain, shortness of breath, dizziness, lightheadedness, nausea, sharp or persistent pain, or any warning sign — and seek immediate medical attention.</li>
              <li>"Active Recovery", "Mindful Sanctuary", and similar features are NOT physical therapy, rehabilitation, or post-injury care. Do not use them in place of professional care.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4a. Assumption of Risk &amp; Release of Liability</h2>
            <p>
              By using Anvil &amp; Ash, you knowingly and voluntarily assume full responsibility
              and all risk of injury, illness, death, property damage, or any adverse outcome
              resulting from your use of the App or any activity inspired by it. You release,
              waive, and hold harmless Anvil &amp; Ash, its owners, operators, contributors,
              employees, contractors, and affiliates from any and all claims, liability, damages,
              costs, or losses — direct, indirect, incidental, consequential, or otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Use the App for any unlawful purpose.</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts.</li>
              <li>Upload harmful, abusive, or inappropriate content.</li>
              <li>Interfere with or disrupt the App's functionality.</li>
              <li>Reverse engineer, decompile, or attempt to extract source code.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">6. Affiliate Links & Products</h2>
            <p>
              The Training Essentials section contains affiliate product recommendations. As an Amazon
              Associate, Anvil & Ash earns from qualifying purchases. Product prices, availability,
              and ratings are subject to change. We do not guarantee product quality or suitability.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">7. Intellectual Property</h2>
            <p>
              All content, design, code, and branding of Anvil & Ash are the property of Anvil & Ash
              and its creators. You may not reproduce, distribute, or create derivative works without
              express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Anvil & Ash and its creators shall not be
              liable for any indirect, incidental, special, consequential, or punitive damages
              arising from your use of the App, including but not limited to physical injury,
              data loss, or financial loss.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">9. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account at our discretion if you
              violate these Terms. Upon termination, your right to use the App ceases immediately.
              You may also delete your account at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">10. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. Material changes will be communicated through
              the App. Continued use after changes constitutes acceptance. If you disagree with
              updated terms, please discontinue use and contact us for account deletion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">11. Contact</h2>
            <p>
              For questions about these Terms, contact us at:
            </p>
            <p className="mt-2 text-blood-400 font-semibold">support@anvilash.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
